/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, DynamicSelect, DynamicSearchSelect, IModalComponentProps } from './form-control';

interface IFormValues {
    payment_term: {
        name: string;
        due_date_based_on: number | null;
        credit_days: number | null;
        mode_of_payment: string;
        discount_type: string;
        discount: number | null;
        discount_due_date_based_on: number | null;
        discount_validity: number | null;
        description: string;
    };
}

const validationSchema = Yup.object().shape({
    payment_term: Yup.object().shape({
        name: Yup.string().required('Name is required'),
        due_date_based_on: Yup.number()
            .typeError('Due date based on is required')
            .required('Due date based on is required'),
        credit_days: Yup.number()
            .typeError('Credit days is required')
            .required('Credit days is required')
            .positive('Must be positive')
            .integer('Must be a whole number'),
        mode_of_payment: Yup.string().nullable(),
        discount_type: Yup.string().nullable(),
        discount: Yup.number().nullable().typeError('Must be a number'),
        discount_due_date_based_on: Yup.number().nullable().typeError('Must be a number'),
        discount_validity: Yup.number().nullable().typeError('Must be a number').integer('Must be a whole number'),
        description: Yup.string().nullable(),
    })
});

const MODE_OF_PAYMENT_OPTIONS = [
    { label: 'Bank Draft', value: 'BANK_DRAFT' },
    { label: 'Cash', value: 'CASH' },
    { label: 'Credit Card', value: 'CREDIT_CARD' },
    { label: 'Wire Transfer', value: 'WIRE_TRANSFER' }
];

const DISCOUNT_TYPE_OPTIONS = [
    { label: 'Percentage', value: 'PERCENTAGE' },
    { label: 'Amount', value: 'AMOUNT' }
];

export const PaymentTermAddModal: React.FC<IModalComponentProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, trigger } = useForm<any>({
        mode: 'all',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            payment_term: {
                name: '',
                due_date_based_on: null,
                credit_days: null,
                mode_of_payment: '',
                discount_type: '',
                discount: null,
                discount_due_date_based_on: null,
                discount_validity: null,
                description: '',
            }
        }
    });

    const handleModalClose = () => {
        reset({
            payment_term: {
                name: '',
                due_date_based_on: null,
                credit_days: null,
                mode_of_payment: '',
                discount_type: '',
                discount: null,
                discount_due_date_based_on: null,
                discount_validity: null,
                description: '',
            }
        });
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave(values?.payment_term as any);
        handleModalClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='md'
            fullWidth
            {...modalProps}
        >
            <>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {isLoading && <ModalLoader />}
                    <DialogTitle className='paymentTermAddModal--DialogTitle'>
                        <Typography type='s3' color='inherit' weight='medium'>
                            {t("common.paymentTerm.title")}
                        </Typography>
                        <IconButton onClick={handleModalClose}>
                            <Close fontSize='small' />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className='paymentTermAddModal--DialogContents'>
                        <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                            <Grid item xs={12} sm={6}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("common.paymentTerm.fields.name_label")}
                                    name='name'
                                    fieldArrayName='payment_term'
                                    placeholder={t("common.paymentTerm.fields.name_placeholder")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicSearchSelect
                                    label={t("common.paymentTerm.fields.due_date_based_on_label")}
                                    apiType='dueDateType'
                                    name='due_date_based_on'
                                    fieldArrayName='payment_term'
                                    formControl={control}
                                    placeholder={t("common.paymentTerm.fields.due_date_based_on_placeholder")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicInput
                                    type='number'
                                    formControl={control}
                                    label={t("common.paymentTerm.fields.credit_days_label")}
                                    name='credit_days'
                                    fieldArrayName='payment_term'
                                    placeholder={t("common.paymentTerm.fields.credit_days_placeholder")}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicSelect
                                    label={t("common.paymentTerm.fields.mode_of_payment_label")}
                                    name='mode_of_payment'
                                    fieldArrayName='payment_term'
                                    formControl={control}
                                    placeholder={t("common.paymentTerm.fields.mode_of_payment_placeholder")}
                                    options={MODE_OF_PAYMENT_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicSelect
                                    label={t("common.paymentTerm.fields.discount_type_label")}
                                    name='discount_type'
                                    fieldArrayName='payment_term'
                                    formControl={control}
                                    placeholder={t("common.paymentTerm.fields.discount_type_placeholder")}
                                    options={DISCOUNT_TYPE_OPTIONS}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicInput
                                    type='number'
                                    formControl={control}
                                    label={t("common.paymentTerm.fields.discount_label")}
                                    name='discount'
                                    fieldArrayName='payment_term'
                                    placeholder={t("common.paymentTerm.fields.discount_placeholder")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicSearchSelect
                                    label={t("common.paymentTerm.fields.discount_due_date_based_on_label")}
                                    apiType='dueDateType'
                                    name='discount_due_date_based_on'
                                    fieldArrayName='payment_term'
                                    formControl={control}
                                    placeholder={t("common.paymentTerm.fields.discount_due_date_based_on_placeholder")}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DynamicInput
                                    type='number'
                                    formControl={control}
                                    label={t("common.paymentTerm.fields.discount_validity_label")}
                                    name='discount_validity'
                                    fieldArrayName='payment_term'
                                    placeholder={t("common.paymentTerm.fields.discount_validity_placeholder")}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DynamicInput
                                    type='text'
                                    formControl={control}
                                    label={t("common.paymentTerm.fields.description_label")}
                                    name='description'
                                    fieldArrayName='payment_term'
                                    placeholder={t("common.paymentTerm.fields.description_placeholder")}
                                    is_multiline
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className='paymentTermAddModal--DialogAction'>
                        <span></span>
                        <Box display='flex' gap={1.5}>
                            <Button
                                className='paymentTermAddModal--DialogAction--WhiteBtn'
                                onClick={handleModalClose}
                            >
                                {t("common.cancel")}
                            </Button>
                            <Button type='button' onClick={handleSubmit(onSubmit)}>
                                {t("common.save")}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </>
        </Modal>
    );
};
